// Craft maker mini-game: follow the color pattern (simon-says lite)
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

const COLORS = [
  { id: "pink", bg: "bg-pink-400", hover: "hover:bg-pink-500", light: "bg-pink-200", emoji: "🌸" },
  { id: "blue", bg: "bg-blue-400", hover: "hover:bg-blue-500", light: "bg-blue-200", emoji: "💙" },
  { id: "yellow", bg: "bg-yellow-400", hover: "hover:bg-yellow-500", light: "bg-yellow-200", emoji: "⭐" },
  { id: "green", bg: "bg-emerald-400", hover: "hover:bg-emerald-500", light: "bg-emerald-200", emoji: "🍀" },
];

const CraftGame = ({ onFinish }) => {
  const [pattern, setPattern] = useState([]);
  const [userStep, setUserStep] = useState(0);
  const [flashing, setFlashing] = useState(null);
  const [showing, setShowing] = useState(false);
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [round, setRound] = useState(0);
  const [done, setDone] = useState(false);

  const startRound = () => {
    const nextLen = pattern.length + 1;
    const p = Array.from({ length: nextLen }, () => COLORS[Math.floor(Math.random() * COLORS.length)].id);
    setPattern(p);
    setRound((r) => r + 1);
    setUserStep(0);
    showPattern(p);
  };

  const showPattern = (p) => {
    setShowing(true);
    p.forEach((c, i) => {
      setTimeout(() => setFlashing(c), 600 * (i + 1));
      setTimeout(() => setFlashing(null), 600 * (i + 1) + 350);
    });
    setTimeout(() => setShowing(false), 600 * (p.length + 1));
  };

  useEffect(() => {
    startRound();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (round >= 5 || misses >= 3) {
      setDone(true);
      const rating = Math.max(1, Math.min(5, 2 + score - misses));
      setTimeout(() => onFinish({ score, misses, rating }), 400);
    }
    // eslint-disable-next-line
  }, [round, misses]);

  const handleTap = (id) => {
    if (showing || done) return;
    setFlashing(id);
    setTimeout(() => setFlashing(null), 200);
    if (pattern[userStep] === id) {
      const next = userStep + 1;
      if (next === pattern.length) {
        setScore((s) => s + 1);
        setTimeout(() => startRound(), 700);
      } else {
        setUserStep(next);
      }
    } else {
      setMisses((m) => m + 1);
      setTimeout(() => showPattern(pattern), 400);
      setUserStep(0);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex justify-between items-center mb-3 font-heading font-black">
        <div className="bg-pink-100 border-2 border-pink-300 rounded-xl px-3 py-1 text-pink-800" data-testid="craft-score">
          🎨 {score}
        </div>
        <div className="bg-rose-100 border-2 border-rose-300 rounded-xl px-3 py-1 text-rose-800">
          ❌ {misses}
        </div>
        <div className="bg-amber-100 border-2 border-amber-300 rounded-xl px-3 py-1 text-amber-800">
          Round {round}/5
        </div>
      </div>
      <p className="text-center text-sm text-slate-600 font-semibold mb-3">
        Watch the bead pattern, then tap the colors in the same order!
      </p>
      <div className="bg-pink-50 border-4 border-pink-300 rounded-3xl p-6 mb-4">
        <div className="text-center text-xs font-black uppercase text-pink-700 mb-2">
          {showing ? "Watch..." : done ? "Great job!" : "Your turn!"}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {COLORS.map((c) => (
            <button
              key={c.id}
              onClick={() => handleTap(c.id)}
              disabled={showing || done}
              data-testid={`craft-color-${c.id}`}
              className={`aspect-square rounded-2xl text-5xl font-black flex items-center justify-center border-4 transition-all ${
                flashing === c.id ? `${c.bg} scale-105 border-white shadow-2xl` : `${c.light} border-transparent hover:${c.bg}`
              }`}
            >
              {c.emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CraftGame;
